"use client";

import { useEffect, useState } from "react";
import { useDocumentSet } from "@/lib/document-set-context";
import { useToast } from "@/components/toast-provider";
import * as s from "@/lib/styles";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, create a member set under this group (not a group). */
  parentId?: number | null;
}

/** Create a document set. Top-level create must choose group vs set. */
export default function CreateDocumentSetModal({ open, onClose, parentId }: Props) {
  const { showToast } = useToast();
  const { sets, createSet, selectSet } = useDocumentSet();

  const [name, setName] = useState("");
  const [isGroup, setIsGroup] = useState<boolean | null>(null);
  const [creating, setCreating] = useState(false);

  const parentSet =
    parentId != null && parentId > 0 ? sets.find((s) => s.id === parentId) : null;
  const nesting = Boolean(parentSet);

  useEffect(() => {
    if (!open) return;
    setName("");
    setIsGroup(null);
  }, [open, parentId]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !creating) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, creating, onClose]);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!nesting && isGroup === null) return;
    setCreating(true);
    try {
      const parentNum = nesting ? parentSet!.id : null;
      const info = await createSet(trimmed, parentNum, nesting ? false : Boolean(isGroup));
      await selectSet(info.id);
      const label = parentSet ? `${parentSet.name} / ${info.name}` : info.name;
      showToast("success", `Created: ${label}`);
      onClose();
      window.location.href = "/documents";
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Create failed");
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  const canSubmit = Boolean(name.trim()) && (nesting || isGroup !== null);

  return (
    <div
      className={s.modalOverlay + " z-50 p-4"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-set-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !creating) onClose();
      }}
    >
      <div className={s.modalContent}>
        <div className="flex items-center justify-between gap-3">
          <h2 id="create-set-title" className="text-base font-semibold text-purple m-0">
            {nesting ? `New set in ${parentSet?.name}` : "New document set"}
          </h2>
          <button
            type="button"
            className="text-sm text-muted hover:text-accent disabled:opacity-40"
            disabled={creating}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div>
          <label htmlFor="set-name" className="block text-sm text-muted mb-1">
            Name
          </label>
          <input
            id="set-name"
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder={nesting ? "e.g. Quiet Nights" : "e.g. Accounting"}
            disabled={creating}
            className={s.input + " w-full"}
            aria-label="Document set name"
          />
        </div>

        {!nesting && (
          <fieldset className="m-0 p-0 border-0">
            <legend className="block text-sm text-muted mb-2">Is this a group?</legend>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsGroup(true)}
                disabled={creating}
                className={isGroup === true ? s.btnPrimary : s.btnAuxiliary}
                aria-pressed={isGroup === true}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setIsGroup(false)}
                disabled={creating}
                className={isGroup === false ? s.btnPrimary : s.btnAuxiliary}
                aria-pressed={isGroup === false}
              >
                No
              </button>
            </div>
            <p className="text-xs text-muted mt-2 m-0">
              A group can contain other sets (for example Accounting). A regular
              set just holds documents.
            </p>
          </fieldset>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            className={s.btnPrimary}
            disabled={creating || !canSubmit}
            onClick={() => void submit()}
          >
            {creating ? "Creating…" : "Create"}
          </button>
          <button
            type="button"
            className={s.btnAuxiliary}
            disabled={creating}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
